import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import type { FilterConfig } from "../types";
import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import { ASSIGNEE_PRINCIPAL_FIELD } from "../build-where";
import {
  STRING_OPERATORS,
  ID_OPERATORS,
  ID_ARRAY_OPERATORS,
  DATE_OPERATORS,
  BOOLEAN_OPERATORS,
  hasSome,
  isIn,
  nin,
  isNull,
  isNotNull,
} from "../operators";

// `satisfies` rather than an annotation: `FilterConfig.fields` is a
// `Record<string, …>`, and annotating would widen these literal keys away, so
// every derived config (the map's subset) would read `FilterFieldDef |
// undefined` for a field written out right here.
export const issueFilterConfig = {
  category: "issues",
  searchableFields: ["name", "description"],
  quickFilters: [
    // Two questions: Assignee is the PEOPLE doing the work (`assignees`);
    // Teams is the project's own Teams property (`teamIds`). A team's work is
    // what carries the team — there is no "anyone on this team" filter.
    {
      field: ASSIGNEE_PRINCIPAL_FIELD,
      group: "People",
      label: "Assignee",
      type: "entity-assignee",
      operator: "hasSome",
      icon: ASSIGNEE_ICON,
    },
    {
      field: "teamIds",
      group: "People",
      label: "Teams",
      type: "entity-team",
      operator: "hasSome",
      icon: PROPERTY_ICON.team,
    },
    {
      field: "issueTypeId",
      group: "Project",
      label: "Project Type",
      type: "issue-type",
      operator: "in",
      icon: PROPERTY_ICON.issueType,
    },
    {
      field: "workflow-stage",
      group: "Project",
      label: "Workflow",
      type: "workflow-stage",
      operator: "in",
      icon: PROPERTY_ICON.workflow,
    },
    {
      field: "age.status",
      group: "Activity",
      label: "Follow up",
      type: "age-status",
      operator: "in",
      icon: PROPERTY_ICON.followUp,
    },
    {
      field: "lastUpdateDate",
      group: "Activity",
      label: "Last updated",
      type: "last-updated",
      operator: "eq",
      icon: "calendar-clock",
    },
    // One flag, so the row names the property and its switch carries the
    // state.
    {
      field: "archived",
      label: "Archived",
      type: "more-options",
      operator: "eq",
      icon: "archive",
    },
  ],
  fields: {
    name: {
      field: "name",
      label: "Name",
      type: "string",
      operators: STRING_OPERATORS,
    },
    description: {
      field: "description",
      label: "Description",
      type: "string",
      operators: STRING_OPERATORS,
    },
    stageId: {
      field: "stageId",
      label: "Stage",
      type: "stage",
      operators: [isIn("Includes any of"), nin("Excludes"), isNull("Is empty"), isNotNull("Is not empty")],
    },
    workflowId: {
      field: "workflowId",
      label: "Workflow",
      type: "workflow",
      operators: ID_OPERATORS,
    },
    issueTypeId: {
      field: "issueTypeId",
      label: "Project Type",
      type: "issue-type",
      operators: [isIn("Includes any of"), nin("Excludes"), isNull("Is empty"), isNotNull("Is not empty")],
    },
    [ASSIGNEE_PRINCIPAL_FIELD]: {
      field: ASSIGNEE_PRINCIPAL_FIELD,
      label: "Assignee",
      type: "entity-assignee",
      // The value filters a nested ref rather than a column, so only the
      // membership operator is expressible here.
      operators: [hasSome("Includes any of")],
    },
    teamIds: {
      field: "teamIds",
      label: "Teams",
      type: "entity-team",
      operators: ID_ARRAY_OPERATORS,
    },
    creationDate: {
      field: "creationDate",
      label: "Created",
      type: "date",
      operators: DATE_OPERATORS,
    },
    archived: {
      field: "archived",
      label: "Archived",
      type: "boolean",
      operators: BOOLEAN_OPERATORS,
    },
    "age.status": {
      field: "age.status",
      label: "Follow up",
      type: "age-status",
      operators: [isIn("Includes any of"), nin("Excludes")],
    },
    lastUpdateDate: {
      field: "lastUpdateDate",
      label: "Last Updated",
      type: "date",
      operators: DATE_OPERATORS,
    },
  },
} satisfies FilterConfig;
